import { useQuery } from "@tanstack/solid-query";
import { isActiveCrawlRunStatus } from "contracts/enums/core";
import { api } from "../../store/api";
import { RUN_POLL_MS } from "../../store/pollCadence";
import { queryClient } from "../../store/query";

/**
 * The single home for every playground query key, poll hook, and invalidation —
 * mirrors `features/runs/runQueries.ts`. Components import these instead of
 * hand-writing key arrays, so the cache namespaces can't drift (e.g. the run
 * output key was previously built two different ways across two files).
 */

const playgroundWorkspacesKey = () => ["playground-workspaces"] as const;
const playgroundRunsKey = (websiteId: string) =>
  ["playground-runs", websiteId] as const;
const runOutputKey = (websiteId: string, runId: string | null) =>
  ["playground-run-output", websiteId, runId ?? "none"] as const;
const promptBundlesKey = () => ["prompt-bundles"] as const;
const playgroundDraftsKey = () => ["playground-drafts"] as const;

// The two editor routes keep bespoke queries; they import just the key builder.
export const promptEditorKey = (bundleId: string | undefined) =>
  ["prompt-editor", bundleId ?? "new"] as const;
export const draftEditorKey = (draftId: string | undefined) =>
  ["playground-draft-editor", draftId ?? "new"] as const;

// Poll cadence while a run is active; off once nothing is running.

export function createPlaygroundWorkspaces() {
  return useQuery(() => ({
    queryKey: playgroundWorkspacesKey(),
    queryFn: () => api.playground.listWorkspaces(),
  }));
}

export function createPlaygroundRuns(websiteId: string) {
  return useQuery(() => ({
    queryKey: playgroundRunsKey(websiteId),
    queryFn: () => api.playground.listRuns(websiteId),
    refetchInterval: (query) =>
      query.state.data?.runs.some((run) => isActiveCrawlRunStatus(run.status))
        ? RUN_POLL_MS
        : false,
  }));
}

export function createRunOutput(
  websiteId: () => string,
  runId: () => string | null,
) {
  return useQuery(() => ({
    queryKey: runOutputKey(websiteId(), runId()),
    queryFn: () => api.playground.runOutput(websiteId(), runId() as string),
    enabled: runId() !== null,
    refetchInterval: (query) =>
      isActiveCrawlRunStatus(query.state.data?.run.status)
        ? RUN_POLL_MS
        : false,
  }));
}

export function createPromptBundles() {
  return useQuery(() => ({
    queryKey: promptBundlesKey(),
    queryFn: () => api.prompts.listBundles(),
  }));
}

export function createPlaygroundDrafts() {
  return useQuery(() => ({
    queryKey: playgroundDraftsKey(),
    queryFn: () => api.playground.listDrafts(),
  }));
}

export function createImportableVoices() {
  return useQuery(() => ({
    queryKey: ["playground-voices"] as const,
    queryFn: () => api.playground.listVoices(),
  }));
}

export const invalidateWorkspaces = () =>
  queryClient.invalidateQueries({ queryKey: playgroundWorkspacesKey() });
export const invalidateRuns = (websiteId: string) =>
  queryClient.invalidateQueries({ queryKey: playgroundRunsKey(websiteId) });
export const invalidatePromptBundles = () =>
  queryClient.invalidateQueries({ queryKey: promptBundlesKey() });
export const invalidateDrafts = () =>
  queryClient.invalidateQueries({ queryKey: playgroundDraftsKey() });
